import type { BrandingClientSet } from './types';

const DEFAULT_DEV_API_ORIGIN = 'https://dev-api.mimecast-dev.com';

type BrandingLogoLink = { href?: string };

/** Window-preloaded branding may use legacy link objects; branding client uses strings. */
type BrandingSetWithLegacyLogos = Omit<BrandingClientSet, 'loginPageLogo'> & {
  mainPageLogo?: BrandingLogoLink;
  loginPageLogo?: string | BrandingLogoLink;
};

const BRANDING_STYLESHEET_LINK_ID = 'mimecast-branding-stylesheet';

type BrandingSetWithCssPath = BrandingClientSet & {
  cssPath?: { href?: string };
};

export function applyBrandingFavicon(
  doc: Document,
  brandingSet: BrandingClientSet
): void {
  if (!brandingSet.branded || !brandingSet.favIcon) {
    return;
  }

  const isLocalDev = isLocalDevHost(doc);

  setLinkHref(
    doc,
    'icon',
    resolveBrandingAssetUrl(brandingSet.favIcon, isLocalDev)
  );

  if (brandingSet.favIconMobile) {
    setLinkHref(
      doc,
      'apple-touch-icon',
      resolveBrandingAssetUrl(brandingSet.favIconMobile, isLocalDev)
    );
  }
}

/** Prefer cssPath.href when present; otherwise derive style.css from /branding asset paths. */
export function resolveBrandingStylesheetUrl(
  brandingSet: BrandingClientSet,
  isLocalDev: boolean
): string | null {
  if (!brandingSet.branded) {
    return null;
  }

  const cssPathHref = (brandingSet as BrandingSetWithCssPath).cssPath?.href;
  if (cssPathHref) {
    return resolveBrandingAssetUrl(cssPathHref, isLocalDev);
  }

  const assetUrl =
    extractBrandingLogoHref(brandingSet) || brandingSet.favIcon || '';
  const legacyBrandingPath = parseLegacyBrandingAssetUrl(assetUrl);

  if (!legacyBrandingPath) {
    return null;
  }

  return resolveBrandingAssetUrl(
    `${legacyBrandingPath.basePath}/style.css${legacyBrandingPath.query}`,
    isLocalDev
  );
}

export function applyBrandingStylesheet(
  doc: Document,
  brandingSet: BrandingClientSet
): void {
  const isLocalDev = isLocalDevHost(doc);
  const href = resolveBrandingStylesheetUrl(brandingSet, isLocalDev);
  const existing = doc.getElementById(
    BRANDING_STYLESHEET_LINK_ID
  ) as HTMLLinkElement | null;

  if (!href) {
    existing?.remove();
    return;
  }

  const link = existing ?? doc.createElement('link');
  link.id = BRANDING_STYLESHEET_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;

  if (!existing) {
    doc.head.appendChild(link);
  }
}

function setLinkHref(doc: Document, rel: string, href: string): void {
  const selector = `link[rel="${rel}"]`;
  const existing = Array.from(doc.querySelectorAll<HTMLLinkElement>(selector));
  let link = existing[0];

  if (!link) {
    link = doc.createElement('link');
    link.rel = rel;
    doc.head.appendChild(link);
  }

  link.href = href;

  if (rel === 'icon') {
    link.type = '';
    link.removeAttribute('sizes');
    existing.slice(1).forEach((extraLink) => extraLink.remove());
  }
}

/** Prefer mainPageLogo.href when present; branding client uses string logo fields. */
export function extractBrandingLogoHref(
  brandingSet: BrandingClientSet
): string {
  const branding = brandingSet as BrandingSetWithLegacyLogos;

  if (branding.mainPageLogo?.href) {
    return branding.mainPageLogo.href;
  }

  if (branding.logo) {
    return branding.logo;
  }

  const loginPageLogo = branding.loginPageLogo;
  if (typeof loginPageLogo === 'string') {
    return loginPageLogo;
  }

  return loginPageLogo?.href ?? '';
}

/** Local dev can load branding images directly from dev-api when proxy paths fail. */
export function resolveBrandingAssetUrl(
  url: string,
  isLocalDev: boolean
): string {
  if (!isLocalDev) {
    return url;
  }

  const brandingAssetPath = parseBrandingAssetsPath(url);
  if (brandingAssetPath) {
    return `${DEFAULT_DEV_API_ORIGIN}/branding/${brandingAssetPath}`;
  }

  if (url.startsWith('http://') && url.includes('mimecast-dev.com')) {
    return url.replace(/^http:/, 'https:');
  }

  // `/branding/{hash}/main-page-logo.png?tkn=...` is proxied to security-dev in dev.
  return url;
}

/** `/branding/{hash}/main-page-logo.png?tkn=...` → `{ basePath, query }` for style.css derivation. */
function parseLegacyBrandingAssetUrl(
  assetUrl: string
): { basePath: string; query: string } | null {
  if (!assetUrl.startsWith('/branding/')) {
    return null;
  }

  const queryIndex = assetUrl.indexOf('?');
  const path = queryIndex === -1 ? assetUrl : assetUrl.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : assetUrl.slice(queryIndex);

  const segments = path.split('/').filter(Boolean);
  if (segments.length !== 3 || segments[0] !== 'branding') {
    return null;
  }

  const [, tenantId, filename] = segments;
  if (!tenantId || !filename) {
    return null;
  }

  return { basePath: `/branding/${tenantId}`, query };
}

/** `/branding-assets/{env}/{customer}/{assetId}?v=...` → `{customer}/{assetId}` */
function parseBrandingAssetsPath(url: string): string | null {
  const prefix = '/branding-assets/';
  if (!url.startsWith(prefix)) {
    return null;
  }

  const pathWithoutQuery = url.split('?')[0];
  const remainder = pathWithoutQuery.slice(prefix.length);
  const slashIndex = remainder.indexOf('/');

  if (slashIndex === -1) {
    return null;
  }

  const assetPath = remainder.slice(slashIndex + 1);
  return assetPath || null;
}

function isLocalDevHost(doc: Document): boolean {
  const host = doc.defaultView?.location.hostname ?? '';
  return (
    host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')
  );
}
